import { useState } from "react";
import dayjs from "dayjs";
import { db } from "../lib/api";
import { useQuery } from "../lib/hooks";
import { googleCalendarUrl } from "../components/Appointments";
import { clock, zoneLabel, zoneLocal, type Zone } from "../lib/zones";

type M = {
  title: string; starts_at: string; ends_at: string | null; status: string; meeting_url: string | null; location: string | null;
  first_name: string | null; with: string | null; reply_to: string | null; calendly_reschedule: string | null; calendly_cancel: string | null;
  booking_page: string | null; can_change: boolean; can_accept?: boolean; phone?: string | null; zone_code?: string; zone_label?: string; local_start?: string;
};

/** The page a client opens from their reminder email: see the meeting, cancel it, or pick a new time. No login. */
export default function MeetingPublic({ token }: { token: string }) {
  const q = useQuery(`public-meeting:${token}`, () => db.rpc<M | null>("meeting_public", { p_token: token }), { staleMs: 0 });
  // the email's Reschedule / Cancel buttons open this page on that choice (?a=reschedule | ?a=cancel)
  const [mode, setMode] = useState<"" | "cancel" | "reschedule">(() => {
    const a = new URLSearchParams(window.location.search).get("a");
    return a === "cancel" || a === "reschedule" ? a : "";
  });
  async function accept() {
    setBusy(true); setErr("");
    try {
      const r = await db.rpc<{ ok: boolean }>("meeting_accept", { p_token: token });
      if (!r.ok) { setErr("This time can no longer be accepted here. Please reply to the email."); return; }
      setDone("accepted");
    } catch { setErr("Something went wrong. Please reply to the email instead."); } finally { setBusy(false); }
  }
  const [reason, setReason] = useState("");
  const [done, setDone] = useState<"" | "cancelled" | "rescheduling" | "accepted">("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const m = q.data;

  async function cancel(reschedule: boolean) {
    setBusy(true); setErr("");
    try {
      const r = await db.rpc<{ ok: boolean }>("meeting_cancel", { p_token: token, p_reason: reason || null, p_reschedule: reschedule });
      if (!r.ok) { setErr("This meeting can no longer be changed here. Please reply to the reminder email."); return; }
      if (reschedule && m?.booking_page) { window.location.href = m.booking_page; return; }
      setDone(reschedule ? "rescheduling" : "cancelled");
    } catch { setErr("Something went wrong. Please reply to the reminder email instead."); } finally { setBusy(false); }
  }

  return (
    <div className="pub">
      <div className="pub-card">
        <div className="pub-head"><img src="/logo-email.png" alt="" /><div><b>Royal Compass</b><em>Advisory</em></div></div>
        <div className="pub-body">
          {q.loading && !q.data ? <p className="muted">Loading your meeting…</p> : !m ? (
            <><h1>Link not found</h1><p>This link isn't valid any more. Please reply to the email you received and we'll sort it out.</p></>
          ) : done === "accepted" ? (
            <><h1>You're confirmed</h1>
              <p>Thank you{m.first_name ? `, ${m.first_name}` : ""}. Your meeting is booked for <b style={{ display: "inline" }}>{m.local_start ?? dayjs(m.starts_at).format("dddd, MMMM D")}</b> ({zoneLabel(m.zone_code)}). Your confirmation and calendar invitation are on their way to your inbox.</p>
              {m.meeting_url && <a className="pub-btn" href={m.meeting_url}>Join Zoom meeting</a>}</>
          ) : done ? (
            <><h1>{done === "cancelled" ? "Your meeting is cancelled" : "We'll find a new time"}</h1>
              <p>Thank you for letting us know{m.first_name ? `, ${m.first_name}` : ""}. {done === "cancelled" ? `If you'd like to book again, just reply to the email${m.booking_page ? " or use the button below" : ""}.` : "Reply to the email and we'll suggest a new time."}</p>
              {m.booking_page && <a className="pub-btn" href={m.booking_page}>Book a new time</a>}</>
          ) : (
            <>
              <h1>{m.first_name ? `Hi ${m.first_name},` : "Your meeting"}</h1>
              <div className="pub-meeting">
                <div className="pub-kicker">{m.title}</div>
                <div className="pub-date">{dayjs(zoneLocal(m.starts_at, (m.zone_code ?? "ON") as Zone).slice(0, 10)).format("dddd, MMMM D")}</div>
                <div className="pub-time">{clock(zoneLocal(m.starts_at, (m.zone_code ?? "ON") as Zone))} {zoneLabel(m.zone_code)} · with {m.with ?? "Royal Compass Advisory"}</div>
                {m.status === "Proposed" ? <p className="pub-note">Proposed time: waiting for you to accept.</p> : m.status !== "Scheduled" ? <p className="pub-note">This meeting is {m.status.toLowerCase()}.</p> : <>
                  {m.meeting_url && <a className="pub-btn" href={m.meeting_url}>Join Zoom meeting</a>}
                  {m.location && !m.meeting_url && <p>📍 {m.location}</p>}
                  <a className="pub-link" href={googleCalendarUrl({ title: `${m.title} · Royal Compass Advisory`, start: m.starts_at, end: m.ends_at, location: m.meeting_url ?? m.location })} target="_blank" rel="noopener noreferrer">Add to Google Calendar</a>
                </>}
              </div>
              {m.can_accept && !mode && (
                <div className="pub-actions"><button className="pub-btn" disabled={busy} onClick={accept}>{busy ? "One moment…" : "Accept this time"}</button></div>)}
              {m.can_change && (m.calendly_reschedule || m.calendly_cancel ? (
                <div className="pub-actions">
                  {m.calendly_reschedule && <a className="pub-btn ghost" href={m.calendly_reschedule}>Pick a new time</a>}
                  {m.calendly_cancel && <a className="pub-btn ghost" href={m.calendly_cancel}>Cancel the meeting</a>}
                </div>
              ) : mode === "reschedule" ? (
                <div className="pub-actions col">
                  <p style={{ margin: 0 }}>{m.booking_page ? "Pick a time that suits you better. This meeting will be released once you continue." : "Let us know and we'll suggest a new time. This meeting will be released."}</p>
                  <label>Anything we should know? (optional)<textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} maxLength={500} /></label>
                  <div className="pub-actions">
                    <button className="pub-btn" disabled={busy} onClick={() => cancel(true)}>{busy ? "One moment…" : m.booking_page ? "Choose a new time" : "Ask for a new time"}</button>
                    <button className="pub-btn ghost" onClick={() => setMode("")}>Keep my current time</button>
                  </div>
                </div>
              ) : mode === "cancel" ? (
                <div className="pub-actions col">
                  <label>Anything we should know? (optional)<textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} maxLength={500} /></label>
                  <div className="pub-actions">
                    <button className="pub-btn danger" disabled={busy} onClick={() => cancel(false)}>{busy ? "One moment…" : m.can_accept ? "Yes, decline" : "Yes, cancel the meeting"}</button>
                    <button className="pub-btn ghost" onClick={() => setMode("")}>Keep it</button>
                  </div>
                </div>
              ) : (
                <div className="pub-actions">
                  <button className="pub-btn ghost" disabled={busy} onClick={() => setMode("reschedule")}>{m.can_accept ? "Suggest another time" : "Reschedule"}</button>
                  <button className="pub-btn ghost" onClick={() => setMode("cancel")}>{m.can_accept ? "Decline" : "Cancel the meeting"}</button>
                </div>
              ))}
              {err && <p className="pub-err">{err}</p>}
              {(m.reply_to || m.phone) && <p className="pub-small">Questions? {m.phone && <>Call <a href={`tel:${m.phone.replace(/[^0-9+]/g, "")}`}>{m.phone}</a>{m.reply_to && " or "}</>}{m.reply_to && <>email <a href={`mailto:${m.reply_to}`}>{m.reply_to}</a></>}</p>}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
